import store from 'store'
import { isDevBuild } from 'config/feature-flags'

// 모노토닉(단조시계) 앵커 기반 서버 시간 보정.
// TOTP 코드 = f(secret, 시각)이라 기기 벽시계가 틀어지면 코드가 사이트와 불일치한다.
// 캘리브레이션 시점의 (서버시각, performance.now, Date.now) 3앵커를 저장하고
// 보정시각 = 서버시각 + performance.now() 경과분으로 재구성 — 벽시계를 계산에서 배제하므로
// 사용 중 OS 시계 변경에 면역이다(설계 §확정결정 7, PR6 리뷰 반영).
// 탭 장기 절전으로 단조시계가 멈춘 경우만 일시 부정확 — 다음 API 응답의 재캘리브레이션으로 해소.
// 절전/수동변경은 신호로 구분 불가능하므로 감지 가드를 두지 않는다(어설픈 가드가 면역을 훼손).

// 배너 임계값: TOTP 반 주기(30초) — 이 이상 틀어지면 무보정 앱들은 코드가 어긋나기 시작.
export const CLOCK_DRIFT_BANNER_MS = 30000

const DEBUG_OFFSET_KEY = 'app.debug.totpTimeOffset'

let calibServerMs = null
let calibPerfMs = null
let calibWallMs = null

// QA용 오버라이드(dev 빌드 한정): 기기 시간을 건드리지 않고 드리프트를 흉내낸다.
// 본인 브라우저 localStorage 한정이라 보안 영향 없음(설계 §확정결정 4).
function getDebugOffsetMs() {
  if (!isDevBuild) return null
  const raw = store.get(DEBUG_OFFSET_KEY)
  if (raw == null) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

// 캐시 응답 방어 임계(PR #1492 리뷰): 모노토닉 추정 현재 서버시각보다 이만큼 과거인 Date는
// 캐시에서 서빙된 낡은 응답으로 보고 거부한다. 절전 복원(추정이 과거로 밀림 → 신선 Date는
// 미래 방향)·시계 수동 변경(벽시계 무관)·초 절단(±1s)은 이 가드에 걸리지 않는다 —
// 즉 정당한 재캘리브레이션을 지연시키는 부작용이 없다(이동평균 스무딩과의 차이).
const STALE_DATE_GUARD_MS = 5000

// HTTP Date 헤더(RFC1123)로 캘리브레이션. 파싱 실패는 무시(throw 없음) —
// restClient 응답 처리 본류에 어떤 영향도 주지 않기 위한 계약(설계 §변경범위 4).
export function updateServerTimeOffset(dateHeaderValue) {
  if (!dateHeaderValue) return
  const serverMs = Date.parse(dateHeaderValue)
  if (Number.isNaN(serverMs)) return
  if (calibServerMs != null) {
    const estimatedServerNow = calibServerMs + (performance.now() - calibPerfMs)
    if (serverMs < estimatedServerNow - STALE_DATE_GUARD_MS) return
  }
  calibServerMs = serverMs
  calibPerfMs = performance.now()
  calibWallMs = Date.now()
}

// TOTP 코드 생성용 보정 시각. 미보정이면 기존 동작(벽시계) 폴백 — 백엔드 미배포/로그인 전에도 안전.
export function getCorrectedNow() {
  const debugOffset = getDebugOffsetMs()
  if (debugOffset != null) return Date.now() + debugOffset
  if (calibServerMs == null) return Date.now()
  return calibServerMs + (performance.now() - calibPerfMs)
}

// 배너용 드리프트: 캘리브레이션 시점에 측정된 고정값(벽시계−서버시각, 빠르면 +).
// 경과 중 추정을 하지 않으므로 절전/수동변경 노이즈가 배너에 유입되지 않는다 —
// 수동 변경분은 다음 캘리브레이션의 새 측정값으로 반영된다. 미보정이면 null(경고 금지).
export function getClockDriftMs() {
  const debugOffset = getDebugOffsetMs()
  if (debugOffset != null) return debugOffset
  if (calibServerMs == null) return null
  return calibWallMs - calibServerMs
}

// 테스트 전용: 모듈 스코프 앵커 초기화(테스트 간 오염 방지). 프로덕션 코드에서 호출 금지.
export function resetTimeSyncForTest() {
  calibServerMs = null
  calibPerfMs = null
  calibWallMs = null
}
